import type { LocalOrSavedFile } from '@frontend/api-types';
import { removeByIndex, type Tuple, type XOR } from '@frontend/duck-tape';
import type { MakeInputProps } from '@frontend/forms';
import { makeLocalFileList } from '@frontend/forms';
import type { FileButtonProps as MFileButtonProps } from '@mantine/core';
import { FileButton as MFileButton } from '@mantine/core';
import { XStack, YStack } from '../../../Containers';
import type { IconName } from '../../../Icon/helpers';
import { Icon } from '../../../Icon/Icon';
import { Text } from '../../../Texts/Text';
import type { FilePreviewsProps } from './FilePreviews';
import { FilePreviews } from './FilePreviews';

export type FileInputProps = MakeInputProps<
  LocalOrSavedFile[],
  Pick<MFileButtonProps<true>, 'accept' | 'disabled' | 'name'> & {
    allowedFileTypes: Tuple<FileType>;
    buttonClassName?: string;
    countMaxFiles?: number;
    iconName?: IconName;
    iconSize?: number;
    noLabel?: boolean;
  }
> &
  XOR<
    {
      // For previewing files, unsure if best done in this component or the parent
      previewType?: FilePreviewsProps['type'];
      showPreview?: true;
    },
    {
      previewType: never;
      showPreview?: false;
    }
  >;

type FileType = keyof typeof mimeTypesMap;

export const mimeTypesMap = {
  audio: 'audio/*',
  image: 'image/*',
  pdf: 'application/pdf',
  video: 'video/*',
};

/**
 * This component doesn't handle displaying uploaded files. Use the `FilePreviews` component for that
 * We set multiple to always be true for simplicity purposes so that you're always dealing with an array of files
 */
export const FileInput = ({
  allowedFileTypes,
  buttonClassName,
  countMaxFiles,
  disabled,
  iconName = 'IconFilePlus',
  iconSize,
  label = 'Attach file(s)',
  name,
  noLabel,
  onChange,
  previewType = 'listItems',
  showPreview,
  value,
  ...props
}: FileInputProps) => {
  const isMaxFilesReached = countMaxFiles !== undefined && value.length >= countMaxFiles;
  const isDisabled = disabled || isMaxFilesReached;
  const iconColor = isDisabled ? 'tertiary' : 'primary';

  const onChangeComposed = (newFiles: File[]) => {
    const localFileList = makeLocalFileList(newFiles);
    const totalFiles = value.length + localFileList.length;

    if (countMaxFiles !== undefined && totalFiles > countMaxFiles) {
      // Only take the first N files that would fit within the limit
      const remainingSlots = countMaxFiles - value.length;
      const filesToAdd = localFileList.slice(0, remainingSlots);
      return onChange([...value, ...filesToAdd]);
    }

    return onChange([...value, ...localFileList]);
  };

  const onDelete: FilePreviewsProps['onDelete'] = (index) => onChange(removeByIndex(value, index));

  return (
    <MFileButton
      accept={allowedFileTypes.map((type) => mimeTypesMap[type!]).join(',')}
      inputProps={{
        disabled: isDisabled,
        name,
      }}
      multiple
      onChange={onChangeComposed}
      {...props}
    >
      {(buttonProps) => (
        // Don't want to have weird spacing if <FilePreviews/> is empty/hidden
        <YStack gapY={showPreview && value.length ? 'sm' : 'none'} justifyContent="start">
          <XStack>
            <XStack
              className={buttonClassName ?? 'p-xs rounded-sm'}
              disabled={isDisabled}
              enableHoverEffect
              gap="xs"
              {...buttonProps}
            >
              <Icon color={iconColor} name={iconName} size={iconSize} stroke={1.5} />
              {noLabel || !label ? null : (
                <Text color={iconColor} type="h6">
                  {label}
                </Text>
              )}
            </XStack>
          </XStack>
          {showPreview ? (
            <FilePreviews disabled={disabled} files={value} onDelete={onDelete} type={previewType} />
          ) : null}
        </YStack>
      )}
    </MFileButton>
  );
};
